import {
  Navigation,
  CheckCircle,
  ShoppingBag,
  Car,
  Home,
  CheckCircle2,
  ChevronRight,
  Lock,
} from 'lucide-react';
import { DriverOrderStatus } from '../../shared/models';

// Step metadata mirroring iOS OrderStep (icon + color per step).
export const STEP_META = [
  { status: DriverOrderStatus.NAVIGATE_TO_RESTAURANT, label: 'Navigate to Restaurant', Icon: Navigation, color: 'text-blue-500', ring: 'border-blue-500', tint: 'bg-blue-50 border-blue-200' },
  { status: DriverOrderStatus.ARRIVED_AT_RESTAURANT, label: 'Arrived at Restaurant', Icon: CheckCircle, color: 'text-green-500', ring: 'border-green-500', tint: 'bg-green-50 border-green-200' },
  { status: DriverOrderStatus.PICKED_UP_ORDER, label: 'Picked Up Order', Icon: ShoppingBag, color: 'text-orange-500', ring: 'border-orange-500', tint: 'bg-orange-50 border-orange-200' },
  { status: DriverOrderStatus.ON_MY_WAY, label: 'On My Way to Customer', Icon: Car, color: 'text-purple-500', ring: 'border-purple-500', tint: 'bg-purple-50 border-purple-200' },
  { status: DriverOrderStatus.ARRIVED_AT_CUSTOMER, label: 'Arrived at Customer', Icon: Home, color: 'text-blue-500', ring: 'border-blue-500', tint: 'bg-blue-50 border-blue-200' },
  { status: DriverOrderStatus.DELIVERED, label: 'Delivered', Icon: CheckCircle2, color: 'text-green-600', ring: 'border-green-600', tint: 'bg-green-50 border-green-200' },
];

// Maps an accepted-order status to { current, completed } step indexes,
// mirroring iOS OrderCompleteCardView.initializeViewState exactly
// (the completed set includes the step matching the current status).
export function stepState(status) {
  switch (status) {
    case DriverOrderStatus.ACCEPTED:
      return { current: 0, completed: 0 };
    case DriverOrderStatus.NAVIGATE_TO_RESTAURANT:
      return { current: 0, completed: 1 };
    case DriverOrderStatus.ARRIVED_AT_RESTAURANT:
      return { current: 1, completed: 2 };
    case DriverOrderStatus.PICKED_UP_ORDER:
      return { current: 2, completed: 3 };
    case DriverOrderStatus.ON_MY_WAY:
    case DriverOrderStatus.IN_DELIVERY:
      return { current: 3, completed: 4 };
    case DriverOrderStatus.ARRIVED_AT_CUSTOMER:
      return { current: 4, completed: 5 };
    case DriverOrderStatus.DELIVERED:
    case DriverOrderStatus.COMPLETED:
      return { current: 5, completed: 6 };
    default:
      return { current: 0, completed: 0 };
  }
}

// Next driver status for the big action button.
export const NEXT_STATUS = {
  [DriverOrderStatus.ACCEPTED]: DriverOrderStatus.NAVIGATE_TO_RESTAURANT,
  [DriverOrderStatus.NAVIGATE_TO_RESTAURANT]: DriverOrderStatus.ARRIVED_AT_RESTAURANT,
  [DriverOrderStatus.ARRIVED_AT_RESTAURANT]: DriverOrderStatus.PICKED_UP_ORDER,
  [DriverOrderStatus.PICKED_UP_ORDER]: DriverOrderStatus.ON_MY_WAY,
  [DriverOrderStatus.ON_MY_WAY]: DriverOrderStatus.ARRIVED_AT_CUSTOMER,
  [DriverOrderStatus.IN_DELIVERY]: DriverOrderStatus.ARRIVED_AT_CUSTOMER,
  [DriverOrderStatus.ARRIVED_AT_CUSTOMER]: DriverOrderStatus.DELIVERED,
};

export const NEXT_ACTION_LABEL = {
  [DriverOrderStatus.ACCEPTED]: '🧭 Navigate to Restaurant',
  [DriverOrderStatus.NAVIGATE_TO_RESTAURANT]: '✅ Arrived at Restaurant',
  [DriverOrderStatus.ARRIVED_AT_RESTAURANT]: '🛍️ Picked Up Order',
  [DriverOrderStatus.PICKED_UP_ORDER]: '🚗 On My Way to Customer',
  [DriverOrderStatus.ON_MY_WAY]: '📍 Arrived at Customer',
  [DriverOrderStatus.IN_DELIVERY]: '📍 Arrived at Customer',
  [DriverOrderStatus.ARRIVED_AT_CUSTOMER]: '🎉 Complete Delivery',
};

// Six-step delivery progress card (mirrors OrderCompleteCardView step list).
// Only the entry pair (navigate / arrived at restaurant) or the single next
// step in the chain can be tapped — later steps stay locked until the step
// before them completes, so pickup's QR scan can't be skipped.
export default function WorkflowSteps({ status, onStepTap, disabled = false }) {
  const { completed } = stepState(status);
  return (
    <div className="space-y-2">
      <p className="text-sm font-bold text-gray-900">Delivery Progress</p>
      {STEP_META.map((step, i) => {
        const isCompleted = i < completed;
        const isEnabled = !isCompleted && (i <= 1 || i === completed);
        const isCurrent = i === completed;
        return (
          <button
            key={step.status}
            onClick={() => onStepTap(step.status, i)}
            disabled={disabled || isCompleted || !isEnabled}
            className={`flex w-full items-center gap-3 rounded-lg border px-3 py-2 text-left ${
              isCurrent ? `${step.tint} border` : 'border-gray-200 bg-gray-50'
            }`}
          >
            <step.Icon
              size={20}
              className={isCompleted || isCurrent ? step.color : 'text-gray-400'}
            />
            <span
              className={`flex-1 text-xs font-medium ${
                isCurrent ? 'text-gray-900' : 'text-gray-500'
              } ${isCompleted ? 'line-through' : ''}`}
            >
              {step.label}
            </span>
            {isCompleted ? (
              <CheckCircle2 size={18} className={step.color} />
            ) : !isEnabled ? (
              <Lock size={14} className="text-gray-300" />
            ) : (
              <span
                className={`h-4 w-4 rounded-full border-2 ${
                  isCurrent ? step.ring : 'border-gray-300'
                }`}
              />
            )}
            {isEnabled && <ChevronRight size={14} className="text-gray-300" />}
          </button>
        );
      })}
    </div>
  );
}
